import { defineStore } from "pinia";

import { getCompanyId } from "@/utils/userUtils";
import { filtersToApiParams } from "@/utils/filterUtils";

import activityLogApi from "@/services/api/activityLogApi";

export const useActivityLogStore = defineStore("activityLog", {
  state: () => ({
    companyId: getCompanyId(),

    groups: [],
    summary: {
      total: 0,
      today: 0,
      create: 0,
      update: 0,
      destroy: 0,
    },

    pagination: {
      page: 1,
      pages: 1,
      series: [],
      count: 0,
      from: 0,
      to: 0,
    },

    availableResources: [],
    availableActions: [],
    isLoadingFilters: false,

    dataIsLoading: false,
    isLoadingMore: false,

    requestVersion: 0,
  }),

  actions: {
    async fetchAvailableFilters() {
      this.isLoadingFilters = true;
      try {
        const response = await activityLogApi.availableFilters(this.companyId);
        const result = response.data.result || {};
        this.availableResources = result.target_resources || [];
        this.availableActions = result.actions || [];
      } finally {
        this.isLoadingFilters = false;
      }
    },

    async fetchLogs({ append = false, filters = {}, searchFields = [] } = {}) {
      if (append && (this.isLoadingMore || this.dataIsLoading)) return;

      if (!append) this.requestVersion += 1;
      const requestVersion = this.requestVersion;
      const page = append ? this.pagination.page + 1 : 1;

      if (append) {
        this.isLoadingMore = true;
      } else {
        this.dataIsLoading = true;
      }

      try {
        const params = {
          page,
          time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          ...filtersToApiParams(filters, searchFields),
        };

        const response = await activityLogApi.index(this.companyId, params);
        if (requestVersion !== this.requestVersion) return;

        const newGroups = response.data.result || [];
        this.groups = append ? this.mergeGroups(this.groups, newGroups) : newGroups;
        this.summary = response.data.summary || this.summary;

        const meta = response.data.pagination_meta;
        if (meta) {
          this.pagination = {
            page: meta.page,
            pages: meta.pages,
            series: meta.series,
            count: meta.count,
            from: meta.from,
            to: meta.to,
          };
        }
      } finally {
        if (append) {
          this.isLoadingMore = false;
        } else {
          this.dataIsLoading = false;
        }
      }
    },

    mergeGroups(existing, incoming) {
      const result = [...existing];
      for (const group of incoming) {
        const found = result.find((g) => g.date === group.date);
        if (found) {
          found.events = [...found.events, ...group.events];
        } else {
          result.push(group);
        }
      }
      return result;
    },

    appendLogs(filters, searchFields) {
      return this.fetchLogs({ append: true, filters, searchFields });
    },
  },
});
